/*
	angular-bottom-sheet.component.ts (Angular 20 standalone component)
	  Uso: importalo come componente standalone oppure includilo in un modulo.
	Esempio d'uso nel template del chiamante:
	<app-bottom-sheet
		[open]="isOpen"
		(closeClicked)="onClose()"
	>
		<ng-template>
			<h3>Contenuto dinamico</h3>
			<p>Qualsiasi markup</p>
		</ng-template>
	</app-bottom-sheet>
*/
import { animate, style, transition, trigger } from "@angular/animations";
import { CommonModule } from "@angular/common";
import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	Output,
	TemplateRef,
	ViewEncapsulation,
} from "@angular/core";

@Component({
	selector: "bottom-sheet",
	standalone: true,
	imports: [CommonModule],
	template: `@if (open) {
		<div
			class="sheet-backdrop position-fixed"
			@backdropAnimation
			(click)="onBackdropClick()"
			aria-hidden="true"
		></div>

		<section
			class="sheet-container position-fixed rounded-top-4 pt-5 px-2 px-sm-3 bottom-0 ck-shadow"
			role="dialog"
			aria-modal="true"
			@sheetAnimation
		>
			<button
				type="button"
				class="position-absolute top-0 end-0 p-0"
				(click)="close()"
			>
				<span
					class="close-x rounded-circle d-inline-block mx-3 my-3"
				></span>
			</button>
			<div class="sheet-content pt-3 scrollingShadowV">
				<ng-container *ngTemplateOutlet="template"></ng-container>
			</div>
		</section>
	}`,
	styles: [
		`
			:host {
				position: relative;
				z-index: 1000;
			}

			.sheet-backdrop {
				inset: 0;
				background-color:rgba(0, 0, 0, 0.4);
				backdrop-filter: blur(2px);
			}

			.sheet-container {
				left: 5%;
				width: 90vw;
				background-color:#fff;
				overflow: hidden;
			}

			.sheet-content {
				overflow: auto;
				min-height: 150px;
				max-height: calc(70vh);
			}

			@media (min-width: 992px) {
				:host {
					display: none;
				}
			}
		`,
	],
	animations: [
		trigger("sheetAnimation", [
			transition(":enter", [
				style({ transform: "translateY(100%)" }),
				animate(
					"220ms cubic-bezier(0.0, 0.0, 0.2, 1)",
					style({ transform: "translateY(0)" }),
				),
			]),
			transition(":leave", [
				animate(
					"180ms cubic-bezier(0.4, 0.0, 1, 1)",
					style({ transform: "translateY(100%)" }),
				),
			]),
		]),

		trigger("backdropAnimation", [
			transition(":enter", [
				style({ opacity: 0 }),
				animate("200ms", style({ opacity: 1 })),
			]),
			transition(":leave", [animate("160ms", style({ opacity: 0 }))]),
		]),
	],
	encapsulation: ViewEncapsulation.Emulated,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BottomSheetComponent {
	@Input() open = false;

	@Input() template!: TemplateRef<any>;

	@Output() closeClicked = new EventEmitter<void>();

	close() {
		this.closeClicked.emit();
		// parent is expected to set [open] to false; as convenience we also toggle local state
		this.open = false;
	}

	onBackdropClick() {
		this.close();
	}
}
